import React, { useState } from "react";
import { Upload, message, Button, Progress, List, Typography } from "antd";
import { 
  UploadOutlined, 
  DeleteOutlined, 
  FileOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined
} from "@ant-design/icons";
import "./style.scss";

const { Dragger } = Upload;
const { Text } = Typography;

const UniversalUpload = ({
  maxFiles = 10,
  maxSize = 25, // MB
  acceptedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.csv', '.jpg', '.jpeg', '.png', '.gif'],
  onFilesChange,
  initialFiles = [],
  disabled = false,
  showProgress = true,
  showFileList = true,
  dragText = "Drag files here or click to upload",
  hintText = null
}) => {
  const [fileList, setFileList] = useState(initialFiles);
  const [uploading, setUploading] = useState(false);

  // Get file icon based on file type
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
      case 'doc':
      case 'docx':
        return <FileWordOutlined style={{ color: '#1890ff' }} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <FileExcelOutlined style={{ color: '#52c41a' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return <FileImageOutlined style={{ color: '#722ed1' }} />;
      default:
        return <FileOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Validate file before upload
  const beforeUpload = (file) => {
    // Check file count
    if (fileList.length >= maxFiles) {
      message.error(`You can only upload up to ${maxFiles} files`);
      return false;
    }

    // Check file type
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    const isValidType = acceptedTypes.includes(fileExtension);
    if (!isValidType) {
      message.error(`File type not supported. Allowed types: ${acceptedTypes.join(', ')}`);
      return false;
    }

    // Check file size
    const maxSizeBytes = maxSize * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      message.error(`File size must be less than ${maxSize}MB`);
      return false;
    }

    // Check for duplicate files
    const isDuplicate = fileList.some(existingFile => 
      existingFile.name === file.name && existingFile.size === file.size
    );
    if (isDuplicate) {
      message.error('This file has already been uploaded');
      return false;
    }

    return false; // Prevent automatic upload, handle manually
  };

  // Handle file selection
  const handleChange = (info) => {
    let newFileList = [...info.fileList];
    
    // Filter out files that failed validation
    newFileList = newFileList.filter(file => {
      if (file.status === 'error') {
        return false;
      }
      return true;
    });

    // Add unique IDs to new files
    newFileList = newFileList.map(file => {
      if (!file.uid) {
        file.uid = Date.now() + Math.random();
      }
      return file;
    });

    setFileList(newFileList);
    
    // Notify parent component
    if (onFilesChange) {
      onFilesChange(newFileList);
    }
  };

  // Remove file
  const handleRemove = (file) => {
    const newFileList = fileList.filter(item => item.uid !== file.uid);
    setFileList(newFileList);
    
    if (onFilesChange) {
      onFilesChange(newFileList);
    }
  };

  // Upload props
  const uploadProps = {
    multiple: true,
    beforeUpload,
    onChange: handleChange,
    onRemove: handleRemove,
    fileList,
    disabled,
    showUploadList: false, // We'll create our own list
  };

  const hintMessage = hintText || `Maximum file size: ${maxSize}MB • Maximum files: ${maxFiles}`;

  return (
    <div className="universal-upload">
      <Dragger {...uploadProps} className="upload-dragger">
        <p className="ant-upload-drag-icon">
          <UploadOutlined style={{ color: "#1890ff", fontSize: "48px" }} />
        </p>
        <p className="ant-upload-text">{dragText}</p>
        <p className="ant-upload-hint">{hintMessage}</p>
      </Dragger>

      {showFileList && fileList.length > 0 && (
        <div className="file-list-container">
          <List
            size="small"
            dataSource={fileList}
            renderItem={(file) => (
              <List.Item
                key={file.uid}
                actions={[
                  <Button
                    type="text"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(file)}
                    disabled={disabled}
                  />
                ]}
              >
                <List.Item.Meta
                  avatar={getFileIcon(file.name)}
                  title={
                    <div className="file-info">
                      <Text ellipsis style={{ maxWidth: 200 }}>{file.name}</Text>
                      <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
                        {formatFileSize(file.size)}
                      </Text>
                    </div>
                  }
                />
                {showProgress && file.status === 'uploading' && (
                  <Progress percent={file.percent} size="small" />
                )}
              </List.Item>
            )}
          />
        </div>
      )}

      {fileList.length > 0 && (
        <div className="upload-summary">
          <Text type="secondary">
            {fileList.length} of {maxFiles} files selected
          </Text>
        </div>
      )}
    </div>
  );
};

export default UniversalUpload;

